import actionTypes from 'Redux/constants';

import { addFlowVersion, getProductLibrary, saveProductLibraryFlow } from 'services/productLibrary';

/**
 * Fetch the full product library for a workspace.
 * Stores categories + flows and the is_empty flag in Redux.
 */
export const fetchProductLibraryAction = workspaceUuid => async dispatch => {
  if (!workspaceUuid) {
    return;
  }

  dispatch({ type: actionTypes.productLibrary.setIsLoading, payload: true });
  dispatch({ type: actionTypes.productLibrary.setError, payload: null });

  try {
    const data = await getProductLibrary(workspaceUuid);

    dispatch({ type: actionTypes.productLibrary.setLibrary, payload: data });
  }
  catch (error) {
    dispatch({
      type: actionTypes.productLibrary.setError,
      payload: error?.message || 'Failed to load product library',
    });
  }
  finally {
    dispatch({ type: actionTypes.productLibrary.setIsLoading, payload: false });
  }
};

/**
 * Save a new flow then re-fetch the library so the UI is up to date.
 *
 * @param {string} workspaceUuid
 * @param {{ name, source, category_name, assets }} payload
 */
export const saveFlowAction = (workspaceUuid, payload) => async dispatch => {
  dispatch({ type: actionTypes.productLibrary.setIsSaving, payload: true });
  dispatch({ type: actionTypes.productLibrary.setError, payload: null });

  try {
    const data = await saveProductLibraryFlow(workspaceUuid, payload);

    // Re-fetch so the library view reflects the new flow immediately
    dispatch(fetchProductLibraryAction(workspaceUuid));

    return data.flow;
  }
  catch (error) {
    dispatch({
      type: actionTypes.productLibrary.setError,
      payload: error?.message || 'Failed to save flow',
    });

    throw error;
  }
  finally {
    dispatch({ type: actionTypes.productLibrary.setIsSaving, payload: false });
  }
};

/**
 * Add a new version to an existing flow then re-fetch the library.
 *
 * @param {string} workspaceUuid
 * @param {string} flowUuid
 * @param {{ source, assets }} payload
 */
export const addFlowVersionAction = (workspaceUuid, flowUuid, payload) => async dispatch => {
  dispatch({ type: actionTypes.productLibrary.setIsSaving, payload: true });
  dispatch({ type: actionTypes.productLibrary.setError, payload: null });

  try {
    const data = await addFlowVersion(workspaceUuid, flowUuid, payload);

    // Re-fetch so the library view reflects the new version immediately
    dispatch(fetchProductLibraryAction(workspaceUuid));

    return data.flow;
  }
  catch (error) {
    dispatch({
      type: actionTypes.productLibrary.setError,
      payload: error?.message || 'Failed to add version',
    });

    throw error;
  }
  finally {
    dispatch({ type: actionTypes.productLibrary.setIsSaving, payload: false });
  }
};
